import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import Header from '../components/Header';
import { API_BASE_URL } from '../utils/api';
import { isAdmin, isEngineer } from '../utils/permissions';

import TaskModal from '../components/TaskModal';
import PhotoUpload from '../components/PhotoUpload';
import { User, Task } from '../types';

const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const [tasks, setTasks] = useState<Task[] | null>(null);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTask, setEditingTask] = useState<Task | undefined>();
  const [isPhotoModalOpen, setIsPhotoModalOpen] = useState(false);
  const [selectedTaskId, setSelectedTaskId] = useState<number | null>(null);
  const [engineerFilter, setEngineerFilter] = useState<number | 'all' | 'unassigned'>('all');
  const [collapsedDates, setCollapsedDates] = useState<Set<string>>(() => {
    // Load collapsed dates from localStorage on mount
    const saved = localStorage.getItem('dashboardCollapsedDates');
    return saved ? new Set(JSON.parse(saved)) : new Set();
  });
  const [collapsedEngineers, setCollapsedEngineers] = useState<Set<string>>(() => {
    // Load collapsed engineers from localStorage on mount
    const saved = localStorage.getItem('dashboardCollapsedEngineers');
    return saved ? new Set(JSON.parse(saved)) : new Set();
  });
  const [dateFilter, setDateFilter] = useState<'today' | 'past' | 'future' | 'all'>('today');
  const [expandedNotes, setExpandedNotes] = useState<Set<number>>(new Set());
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [hasInitializedPastFilter, setHasInitializedPastFilter] = useState(false);

  const userIsAdmin = isAdmin(user);
  const userIsEngineer = isEngineer(user);

  useEffect(() => {
    fetchTasks();
    fetchUsers(); // Now all users (engineers and admins) can see the engineer list
  }, []);

  // Set default filter based on user type
  useEffect(() => {
    if (user) {
      if (userIsAdmin) {
        setEngineerFilter('all');
      } else {
        setEngineerFilter(user.id);
      }
    }
  }, [user, userIsAdmin]);

  // Save collapsed dates to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('dashboardCollapsedDates', JSON.stringify(Array.from(collapsedDates)));
  }, [collapsedDates]);

  // Save collapsed engineers to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('dashboardCollapsedEngineers', JSON.stringify(Array.from(collapsedEngineers)));
  }, [collapsedEngineers]);

  // Auto-collapse dates ONLY on initial load when switching to past filter
  useEffect(() => {
    if (dateFilter === 'past' && !hasInitializedPastFilter && tasks && Array.isArray(tasks)) {
      // Only auto-collapse on first switch to past filter
      const savedCollapsed = localStorage.getItem('dashboardCollapsedDates');
      if (!savedCollapsed) {
        // If no saved state, collapse all dates
        setTimeout(() => {
          const uniqueDates = new Set(tasks.map(task => task.scheduled_date || 'No Date'));
          const taskDates = Array.from(uniqueDates);
          setCollapsedDates(new Set(taskDates));
        }, 100);
      }
      setHasInitializedPastFilter(true);
    } else if (dateFilter !== 'past') {
      // Reset the flag when switching away from past filter
      setHasInitializedPastFilter(false);
    }
  }, [dateFilter, tasks, hasInitializedPastFilter]);

  const fetchTasks = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/tasks`);
      console.log('Tasks API response:', response.data);
      const taskData = response.data;
      if (Array.isArray(taskData)) {
        setTasks(taskData);
      } else {
        console.warn('Tasks API returned non-array data:', taskData);
        setTasks([]);
      }
    } catch (err: any) {
      console.error('Error fetching tasks:', err);
      setError('Failed to fetch tasks');
      setTasks([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/users`);
      console.log('Users API response:', response.data);
      const userData = response.data;
      if (Array.isArray(userData)) {
        setUsers(userData.filter((u: any) => u && u.user_type === 'engineer'));
      } else {
        console.warn('Users API returned non-array data:', userData);
        setUsers([]);
      }
    } catch (err: any) {
      console.error('Failed to fetch users:', err);
      setUsers([]);
    }
  };

  const handleCreateTask = async (taskData: any) => {
    try {
      await axios.post(`${API_BASE_URL}/api/tasks`, taskData);
      setIsModalOpen(false);
      setEditingTask(undefined);
      fetchTasks();
    } catch (err: any) {
      setError('Failed to create task');
    }
  };

  const handleUpdateTask = async (taskData: any) => {
    try {
      await axios.put(`${API_BASE_URL}/api/tasks/${taskData.id}`, taskData);
      setIsModalOpen(false);
      setEditingTask(undefined);
      fetchTasks();
    } catch (err: any) {
      setError('Failed to update task');
    }
  };

  const handleDeleteTask = async (taskId: number) => {
    if (window.confirm('Are you sure you want to delete this task?')) {
      try {
        await axios.delete(`${API_BASE_URL}/api/tasks/${taskId}`);
        fetchTasks();
      } catch (err: any) {
        setError('Failed to delete task');
      }
    }
  };

  const handleEditTask = (task: Task) => {
    setEditingTask(task);
    setIsModalOpen(true);
  };

  const handleViewPhotos = (taskId: number) => {
    setSelectedTaskId(taskId);
    setIsPhotoModalOpen(true);
  };

  const handleTaskSubmit = (taskData: any) => {
    if (editingTask) {
      handleUpdateTask({ ...taskData, id: editingTask.id });
    } else {
      handleCreateTask(taskData);
    }
  };

  const toggleDateCollapse = (date: string) => {
    setCollapsedDates(prev => {
      const newSet = new Set(prev);
      if (newSet.has(date)) {
        newSet.delete(date);
      } else {
        newSet.add(date);
      }
      return newSet;
    });
  };

  const toggleEngineerCollapse = (engineerKey: string) => {
    setCollapsedEngineers(prev => {
      const newSet = new Set(prev);
      if (newSet.has(engineerKey)) {
        newSet.delete(engineerKey);
      } else {
        newSet.add(engineerKey);
      }
      return newSet;
    });
  };

  const toggleNotesExpansion = (taskId: number) => {
    setExpandedNotes(prev => {
      const newSet = new Set(prev);
      if (newSet.has(taskId)) {
        newSet.delete(taskId);
      } else {
        newSet.add(taskId);
      }
      return newSet;
    });
  };

  const shouldTruncateNotes = (notes: string) => {
    if (!notes) return false;
    // Check if notes have more than 2 lines (assuming ~50 chars per line as rough estimate)
    const lines = notes.split('\n');
    return lines.length > 2 || notes.length > 100;
  };

  const getTruncatedNotes = (notes: string) => {
    if (!notes) return '';
    const lines = notes.split('\n');
    if (lines.length <= 2) {
      return notes.length > 100 ? notes.substring(0, 100) + '...' : notes;
    }
    return lines.slice(0, 2).join('\n') + '...';
  };

  if (loading) {
    return (
      <div>
        <Header />
        <div className="container">
          <div style={{ textAlign: 'center', padding: '2rem' }}>Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Header />
      <div className="container">
        <h1 className="page-title">
          {(() => {
            const getDateTitle = () => {
              switch (dateFilter) {
                case 'today': return "Today's Jobs";
                case 'past': return "Past Jobs";
                case 'future': return "Future Jobs";
                case 'all': return "All Jobs";
                default: return "Jobs";
              }
            };
            
            if (userIsAdmin) {
              return getDateTitle();
            } else {
              return engineerFilter === user?.id ? `My ${getDateTitle()}` : getDateTitle();
            }
          })()}
        </h1>

        {error && <div className="error-message">{error}</div>}

        {/* Search and Filter Controls */}
        <div style={{ 
          marginBottom: '1.5rem', 
          display: 'flex', 
          alignItems: 'center', 
          gap: '1rem',
          padding: '1rem',
          backgroundColor: '#f8f9fa',
          borderRadius: '8px',
          border: '1px solid #e9ecef',
          flexWrap: 'wrap'
        }}>
          {/* Search Input */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', minWidth: '250px' }}>
            <label htmlFor="search-input" style={{ fontWeight: '500', color: '#495057', whiteSpace: 'nowrap' }}>
              🔍 Search:
            </label>
            <input
              id="search-input"
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by title, client, or address..."
              style={{
                padding: '0.5rem',
                borderRadius: '4px',
                border: '1px solid #ced4da',
                backgroundColor: 'white',
                fontSize: '0.9rem',
                flex: 1,
                minWidth: '200px'
              }}
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '1.2rem',
                  cursor: 'pointer',
                  color: '#6c757d',
                  padding: '0.25rem'
                }}
                title="Clear search"
              >
                ✕
              </button>
            )}
          </div>

          {/* Engineer Filter */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', minWidth: '200px' }}>
            <label htmlFor="engineer-filter" style={{ fontWeight: '500', color: '#495057', whiteSpace: 'nowrap' }}>
              Filter by Engineer:
            </label>
          <select
            id="engineer-filter"
            value={engineerFilter}
            onChange={(e) => {
              const value = e.target.value;
              if (value === 'all') {
                setEngineerFilter('all');
              } else if (value === 'unassigned') {
                setEngineerFilter('unassigned');
              } else {
                setEngineerFilter(parseInt(value));
              }
            }}
            style={{
              padding: '0.5rem 1rem',
              borderRadius: '4px',
              border: '1px solid #ced4da',
              backgroundColor: 'white',
              fontSize: '0.9rem',
              minWidth: '200px'
            }}
          >
            <option value="all">All Engineers</option>
            {userIsAdmin && <option value="unassigned">Unassigned</option>}
            {userIsAdmin ? (
              users.map(engineer => (
                <option key={engineer.id} value={engineer.id}>
                  {engineer.first_name} {engineer.last_name}
                </option>
              ))
            ) : (
              user && (
                <option key={user.id} value={user.id}>
                  {user.first_name} {user.last_name}
                </option>
              )
            )}
          </select>
          </div>
        </div>

        {/* Date Filter - Only for admins and engineers */}
        {(userIsAdmin || userIsEngineer) && (
          <div style={{ 
            marginBottom: '1.5rem', 
            display: 'flex', 
            alignItems: 'center', 
            gap: '1rem',
            padding: '1rem',
            backgroundColor: '#f8f9fa',
            borderRadius: '8px',
            border: '1px solid #e9ecef'
          }}>
            <label htmlFor="date-filter" style={{ fontWeight: '500', color: '#495057' }}>
              Date Range:
            </label>
            <select
              id="date-filter"
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value as 'today' | 'past' | 'future' | 'all')}
              style={{
                padding: '0.5rem 1rem',
                borderRadius: '4px',
                border: '1px solid #ced4da',
                backgroundColor: 'white',
                fontSize: '0.9rem',
                minWidth: '200px'
              }}
            >
              <option value="today">Today's Jobs</option>
              <option value="past">Past Jobs</option>
              <option value="future">Future Jobs</option>
              <option value="all">All Jobs</option>
            </select>
          </div>
        )}

        {(() => {
          const safeTasks = Array.isArray(tasks) ? tasks : [];
          
          // Helper function to get today's date as YYYY-MM-DD string in local timezone
          const getTodayString = () => {
            const today = new Date();
            const year = today.getFullYear();
            const month = String(today.getMonth() + 1).padStart(2, '0');
            const day = String(today.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
          };
          
          const todayString = getTodayString();
          
          // Filter tasks based on engineer selection and date
          const filteredTasks = safeTasks.filter(task => {
            if (!task || !task.id) return false;
            
            // Exclude invoiced tasks (they go to archived page)
            if (task.status?.toLowerCase() === 'invoiced') {
              return false;
            }
            
            // Search filter
            if (searchTerm.trim()) {
              const searchLower = searchTerm.toLowerCase().trim();
              const matchesSearch = 
                task.title?.toLowerCase().includes(searchLower) ||
                task.client?.toLowerCase().includes(searchLower) ||
                task.address_line1?.toLowerCase().includes(searchLower) ||
                task.address_line2?.toLowerCase().includes(searchLower) ||
                task.postcode?.toLowerCase().includes(searchLower);
              
              if (!matchesSearch) return false;
            }
            
            // Engineer filter
            let passesEngineerFilter = false;
            if (engineerFilter === 'all') {
              passesEngineerFilter = true; // Show all tasks for admin
            } else if (engineerFilter === 'unassigned') {
              passesEngineerFilter = !task.assigned_to; // Show unassigned tasks
            } else {
              passesEngineerFilter = task.assigned_to === engineerFilter; // Show tasks assigned to specific engineer
            }
            
            if (!passesEngineerFilter) return false;
            
            // Date filter - only apply for admins and engineers
            let passesDateFilter = false;
            const taskDate = task.scheduled_date;
            // Extract just the date part from the task date (YYYY-MM-DD)
            const taskDateOnly = taskDate ? taskDate.split('T')[0] : null;
            
            if (!userIsAdmin && !userIsEngineer) {
              // Non-admin/engineer users see all jobs regardless of date filter
              passesDateFilter = true;
            } else if (dateFilter === 'all') {
              passesDateFilter = true;
            } else if (dateFilter === 'today') {
              passesDateFilter = taskDateOnly === todayString;
            } else if (dateFilter === 'past') {
              passesDateFilter = Boolean(taskDateOnly && taskDateOnly < todayString);
            } else if (dateFilter === 'future') {
              passesDateFilter = Boolean(taskDateOnly && taskDateOnly > todayString);
            }
            
            return passesDateFilter;
          });
          
          const hasNoTasks = filteredTasks.length === 0;
          
          if (hasNoTasks) {
            const getDateFilterMessage = () => {
              switch (dateFilter) {
                case 'today': return "today's jobs";
                case 'past': return "past jobs";
                case 'future': return "future jobs";
                case 'all': return "jobs";
                default: return "jobs";
              }
            };
            
            const getEngineerFilterMessage = () => {
              let message = '';
              if (engineerFilter === 'all') {
                message = `No ${getDateFilterMessage()} found`;
              } else if (engineerFilter === 'unassigned') {
                message = `No unassigned ${getDateFilterMessage()} found`;
              } else {
                message = `No ${getDateFilterMessage()} found for the selected engineer`;
              }
              
              if (searchTerm.trim()) {
                message += ` matching "${searchTerm}"`;
              }
              
              return message + '.';
            };
            
            return (
              <div className="card" style={{ textAlign: 'center', padding: '3rem' }}>
                <h3>No tasks found</h3>
                <p style={{ color: '#666', marginBottom: '2rem' }}>
                  {getEngineerFilterMessage()}
                </p>
                {searchTerm.trim() && (
                  <button
                    onClick={() => setSearchTerm('')}
                    className="btn btn-primary"
                    style={{ marginTop: '1rem' }}
                  >
                    Clear search
                  </button>
                )}
              </div>
            );
          }

          // Group filtered tasks by scheduled_date
          const groupedTasks = filteredTasks.reduce((groups: { [key: string]: Task[] }, task) => {
            const date = task.scheduled_date || 'No Date';
            if (!groups[date]) {
              groups[date] = [];
            }
            groups[date].push(task);
            return groups;
          }, {});

          // Sort dates (No Date last)
          const sortedDates = Object.keys(groupedTasks).sort((a, b) => {
            if (a === 'No Date') return 1;
            if (b === 'No Date') return -1;
            // Parse dates as local time to avoid timezone issues
            const parseLocalDate = (dateStr: string) => {
              const parts = dateStr.split('-');
              return new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
            };
            return parseLocalDate(a).getTime() - parseLocalDate(b).getTime();
          });


          const formatDate = (dateString: string) => {
            if (dateString === 'No Date') return 'No Date';
            // Parse the date as local time to avoid timezone issues
            const dateParts = dateString.split('-');
            const date = new Date(parseInt(dateParts[0]), parseInt(dateParts[1]) - 1, parseInt(dateParts[2]));
            return date.toLocaleDateString('en-GB', { 
              weekday: 'long', 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            });
          };

          const formatTime = (timeString: string) => {
            if (!timeString) return '';
            
            // Handle full datetime strings (e.g., "0000-01-01T05:00:00Z")
            if (timeString.includes('T')) {
              const timePart = timeString.split('T')[1];
              if (timePart.includes(':')) {
                const parts = timePart.split(':');
                return `${parts[0]}:${parts[1]}`;
              }
              return timePart;
            }
            
            // Handle simple time strings (e.g., "05:00:00")
            if (timeString.includes(':')) {
              const parts = timeString.split(':');
              return `${parts[0]}:${parts[1]}`;
            }
            
            return timeString;
          };



          const handleStatusChange = async (taskId: number, newStatus: string) => {
            // Check permissions before making the API call
            const task = tasks?.find(t => t.id === taskId);
            if (!userIsAdmin && (!user || !task || task.assigned_to !== user.id)) {
              alert('You can only change the status of tasks assigned to you.');
              return;
            }

            // Engineers can only set status to Booked or Completed
            if (!userIsAdmin && newStatus !== 'Booked' && newStatus !== 'Completed') {
              alert('Engineers can only set tasks to Booked or Completed status.');
              return;
            }

            try {
              console.log('Attempting status update:', { taskId, newStatus });
              console.log('Auth token:', localStorage.getItem('token'));
              console.log('Axios default headers:', axios.defaults.headers.common);
              
              const response = await axios.patch(`${API_BASE_URL}/api/tasks/${taskId}/status`, {
                status: newStatus
              });
              console.log('Status update successful:', response);
              fetchTasks();
            } catch (error: any) {
              console.error('Failed to update task status:', error);
              console.error('Error details:', error.response);
              alert(`Failed to update status: ${error.response?.data?.error || error.message}`);
            }
          };

          const moveTask = async (taskId: number, direction: 'up' | 'down') => {
            // Check permissions before making the API call
            const task = tasks?.find(t => t.id === taskId);
            if (!userIsAdmin && (!user || !task || task.assigned_to !== user.id)) {
              alert('You can only reorder tasks assigned to you.');
              return;
            }

            try {
              const token = localStorage.getItem('token');
              const response = await fetch(`${API_BASE_URL}/api/tasks/${taskId}/order`, {
                method: 'PATCH',
                headers: {
                  'Authorization': `Bearer ${token}`,
                  'Content-Type': 'application/json'
                },
                body: JSON.stringify({ direction })
              });
              
              if (response.ok) {
                fetchTasks();
              } else {
                const errorData = await response.json().catch(() => ({}));
                alert(`Failed to reorder task: ${errorData.error || 'Unknown error'}`);
              }
            } catch (error: any) {
              console.error('Failed to reorder task:', error);
              alert('Network error while reordering task');
            }
          };

          return (
            <div className="task-list-by-date">
              {sortedDates.map(date => {
                const isCollapsed = collapsedDates.has(date);
                return (
                  <div key={date} className="date-group" style={{ marginBottom: '2rem' }}>
                    <h3 
                      className="date-header" 
                      onClick={() => toggleDateCollapse(date)}
                      style={{ 
                        fontSize: '1.25rem', 
                        fontWeight: 'bold', 
                        color: '#2c3e50',
                        marginBottom: '0',
                        padding: '0.75rem',
                        backgroundColor: '#f8f9fa',
                        borderLeft: '4px solid #3498db',
                        borderRadius: isCollapsed ? '4px' : '4px 4px 0 0',
                        cursor: 'pointer',
                        userSelect: 'none',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      <span>
                        📅 {formatDate(date)} ({groupedTasks[date].length} {groupedTasks[date].length === 1 ? 'task' : 'tasks'})
                      </span>
                      <span style={{ 
                        fontSize: '1rem',
                        transform: isCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)',
                        transition: 'transform 0.2s ease'
                      }}>
                        ▼
                      </span>
                    </h3>
                    
                    {!isCollapsed && (
                      <div className="task-list" style={{ 
                        backgroundColor: 'white',
                        border: '1px solid #e9ecef',
                        borderTop: 'none',
                        borderRadius: '0 0 8px 8px',
                        overflow: 'hidden'
                      }}>
                        {/* Group tasks by engineer within each date when showing all engineers */}
                        {engineerFilter === 'all' ? (
                          (() => {
                            // Group tasks for this date by engineer
                            const engineerGroups = groupedTasks[date].reduce((groups: { [key: string]: Task[] }, task) => {
                              const engineerName = task.assigned_user 
                                ? `${task.assigned_user.first_name} ${task.assigned_user.last_name}`
                                : 'Unassigned';
                              if (!groups[engineerName]) {
                                groups[engineerName] = [];
                              }
                              groups[engineerName].push(task);
                              return groups;
                            }, {});
                            
                            return Object.keys(engineerGroups).sort().map(engineerName => {
                              const engineerKey = `${date}-${engineerName}`;
                              const isEngineerCollapsed = collapsedEngineers.has(engineerKey);
                              
                              return (
                              <div key={engineerName} style={{ marginBottom: '1rem' }}>
                                <h4 
                                  onClick={() => toggleEngineerCollapse(engineerKey)}
                                  style={{
                                    margin: '0',
                                    padding: '0.75rem',
                                    backgroundColor: '#e9ecef',
                                    fontWeight: '600',
                                    color: '#495057',
                                    fontSize: '1rem',
                                    borderBottom: '1px solid #dee2e6',
                                    cursor: 'pointer',
                                    userSelect: 'none',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    transition: 'all 0.2s ease'
                                  }}>
                                  <span>
                                    👤 {engineerName} ({engineerGroups[engineerName].length} {engineerGroups[engineerName].length === 1 ? 'task' : 'tasks'})
                                  </span>
                                  <span style={{ 
                                    fontSize: '0.875rem',
                                    transform: isEngineerCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)',
                                    transition: 'transform 0.2s ease'
                                  }}>
                                    ▼
                                  </span>
                                </h4>
                                {!isEngineerCollapsed && engineerGroups[engineerName].map((task, index) => {
                                  const isCompleted = task.status.toLowerCase() === 'completed';
                                  
                                  return (
                                    <div 
                                      key={task.id} 
                                      className="task-list-item" 
                                      style={{ 
                                        padding: '0.75rem',
                                        paddingLeft: '1.5rem',
                                        borderBottom: index < engineerGroups[engineerName].length - 1 ? '1px solid #e9ecef' : 'none',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: '0.5rem',
                                        backgroundColor: 'transparent',
                                        opacity: isCompleted ? 0.6 : 1,
                                        transition: 'opacity 0.3s ease'
                                      }}
                                    >
                                      {/* Task Header */}
                                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '0.5rem' }}>
                                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
                                          {/* Order Position Number */}
                                          <div style={{
                                            backgroundColor: '#007bff',
                                            color: 'white',
                                            borderRadius: '50%',
                                            width: '28px',
                                            height: '28px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            fontSize: '0.875rem',
                                            fontWeight: 'bold',
                                            flexShrink: 0
                                          }}>
                                            {index + 1}
                                          </div>
                                          {/* Task Info */}
                                          <div>
                                            <h4 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 'bold', color: '#2c3e50' }}>
                                              {task.title}
                                            </h4>
                                            <p style={{ margin: '0.25rem 0 0 0', color: '#6c757d', fontSize: '0.9rem' }}>
                                              {task.client}
                                            </p>
                                          </div>
                                        </div>
                                        
                                        {/* Status Selector */}
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                            <span style={{ fontSize: '0.875rem', color: '#666' }}>Status:</span>
                                            <select
                                              value={task.status}
                                              onChange={(e) => userIsAdmin || (user && task.assigned_to === user.id) ? handleStatusChange(task.id, e.target.value) : null}
                                              className="status-select"
                                              disabled={!userIsAdmin && (!user || task.assigned_to !== user.id)}
                                              style={{
                                                padding: '0.25rem 0.5rem',
                                                borderRadius: '4px',
                                                border: '1px solid #ddd',
                                                fontSize: '0.875rem',
                                                backgroundColor: !userIsAdmin && (!user || task.assigned_to !== user.id) ? '#f8f9fa' : 'white',
                                                opacity: !userIsAdmin && (!user || task.assigned_to !== user.id) ? 0.6 : 1,
                                                cursor: !userIsAdmin && (!user || task.assigned_to !== user.id) ? 'not-allowed' : 'pointer'
                                              }}
                                              title={!userIsAdmin && (!user || task.assigned_to !== user.id) ? 'Read-only: Task not assigned to you' : 'Change task status'}
                                            >
                                              {userIsAdmin ? (
                                                <>
                                                  <option value="TBC">TBC</option>
                                                  <option value="Booked">Booked</option>
                                                  <option value="Completed">Completed</option>
                                                  <option value="Invoiced">Invoiced</option>
                                                </>
                                              ) : (
                                                <>
                                                  <option value="Booked">Booked</option>
                                                  <option value="Completed">Completed</option>
                                                  {/* Show current status even if not in allowed list */}
                                                  {task.status !== 'Booked' && task.status !== 'Completed' && (
                                                    <option value={task.status} disabled>{task.status} (Read-only)</option>
                                                  )}
                                                </>
                                              )}
                                            </select>
                                          </div>
                                          
                                          {/* Invoice Number Field - Only show when status is Invoiced */}
                                          {task.status.toLowerCase() === 'invoiced' && (
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                              <span style={{ fontSize: '0.875rem', color: '#666' }}>Invoice Number:</span>
                                              <input
                                                type="text"
                                                value={task.invoice_number || ''}
                                                onChange={async (e) => {
                                                  if (userIsAdmin) {
                                                    try {
                                                      await axios.patch(`${API_BASE_URL}/api/tasks/${task.id}`, {
                                                        invoice_number: e.target.value
                                                      });
                                                      fetchTasks();
                                                    } catch (error: any) {
                                                      console.error('Failed to update invoice number:', error);
                                                      alert('Failed to update invoice number');
                                                    }
                                                  }
                                                }}
                                                disabled={!userIsAdmin}
                                                style={{
                                                  padding: '0.25rem 0.5rem',
                                                  borderRadius: '4px',
                                                  border: '1px solid #ddd',
                                                  fontSize: '0.875rem',
                                                  backgroundColor: !userIsAdmin ? '#f8f9fa' : 'white',
                                                  opacity: !userIsAdmin ? 0.6 : 1,
                                                  cursor: !userIsAdmin ? 'not-allowed' : 'text',
                                                  minWidth: '120px'
                                                }}
                                                placeholder="Enter invoice number"
                                                title={!userIsAdmin ? 'Read-only: Admin access required' : 'Enter invoice number'}
                                              />
                                            </div>
                                          )}
                                        </div>
                                      </div>

                                      {/* Address */}
                                      <div style={{ fontSize: '0.9rem', color: '#495057' }}>
                                        <div>{task.address_line1}</div>
                                        {task.address_line2 && <div>{task.address_line2}</div>}
                                        <div>{task.postcode}</div>
                                      </div>

                                      {/* Time */}
                                      {(task.start_time || task.end_time) && (
                                        <div style={{ 
                                          fontSize: '0.9rem', 
                                          color: '#3498db', 
                                          fontWeight: '500' 
                                        }}>
                                          🕒 {task.start_time ? formatTime(task.start_time) : ''} 
                                          {task.start_time && task.end_time && ' - '}
                                          {task.end_time ? formatTime(task.end_time) : ''}
                                        </div>
                                      )}

                                      {/* Description */}
                                      {task.description && (
                                        <div style={{ 
                                          fontSize: '0.9rem', 
                                          color: '#495057',
                                          backgroundColor: '#f8f9fa',
                                          padding: '0.5rem',
                                          borderRadius: '4px',
                                          borderLeft: '3px solid #6c757d'
                                        }}>
                                          {task.description}
                                        </div>
                                      )}

                                      {/* Engineer Notes */}
                                      {task.engineer_notes && (
                                        <div style={{ 
                                          fontSize: '0.9rem', 
                                          color: '#495057',
                                          backgroundColor: '#e8f4f8',
                                          padding: '0.5rem',
                                          borderRadius: '4px',
                                          borderLeft: '3px solid #17a2b8'
                                        }}>
                                          <div style={{ marginBottom: shouldTruncateNotes(task.engineer_notes) && !expandedNotes.has(task.id) ? '0.5rem' : '0' }}>
                                            <strong style={{ color: '#17a2b8' }}>Engineer Notes:</strong>{' '}
                                            {expandedNotes.has(task.id) || !shouldTruncateNotes(task.engineer_notes) 
                                              ? task.engineer_notes 
                                              : getTruncatedNotes(task.engineer_notes)
                                            }
                                          </div>
                                          {shouldTruncateNotes(task.engineer_notes) && (
                                            <button
                                              onClick={() => toggleNotesExpansion(task.id)}
                                              style={{
                                                background: 'none',
                                                border: 'none',
                                                color: '#17a2b8',
                                                fontSize: '0.875rem',
                                                cursor: 'pointer',
                                                textDecoration: 'underline',
                                                padding: '0',
                                                fontWeight: '500'
                                              }}
                                            >
                                              {expandedNotes.has(task.id) ? 'Read less' : 'Read more'}
                                            </button>
                                          )}
                                        </div>
                                      )}

                                      {/* Assignment */}
                                      <div style={{ fontSize: '0.875rem', color: '#6c757d' }}>
                                        {task.assigned_user ? 
                                          `👤 Assigned to: ${task.assigned_user.first_name} ${task.assigned_user.last_name}` : 
                                          '👤 Unassigned'
                                        }
                                      </div>

                                      {/* Action Buttons */}
                                      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginTop: '0.5rem', justifyContent: 'flex-end' }}>
                                        {/* Task Ordering Buttons */}
                                        {(() => {
                                          // Sort tasks within this engineer's group only
                                          const engineerTasks = engineerGroups[engineerName].sort((a, b) => a.order_position - b.order_position);
                                          const currentIndex = engineerTasks.findIndex(t => t.id === task.id);
                                          const isFirst = currentIndex === 0;
                                          const isLast = currentIndex === engineerTasks.length - 1;
                                          
                                          return (
                                            <>
                                              <button
                                                onClick={() => (userIsAdmin || (user && task.assigned_to === user.id)) && !isFirst ? moveTask(task.id, 'up') : null}
                                                disabled={isFirst || (!userIsAdmin && (!user || task.assigned_to !== user.id))}
                                                style={{
                                                  fontSize: '1rem',
                                                  padding: '0.5rem 0.75rem',
                                                  backgroundColor: (isFirst || (!userIsAdmin && (!user || task.assigned_to !== user.id))) ? '#6c757d' : '#007bff',
                                                  color: 'white',
                                                  border: 'none',
                                                  borderRadius: '4px',
                                                  cursor: (isFirst || (!userIsAdmin && (!user || task.assigned_to !== user.id))) ? 'not-allowed' : 'pointer',
                                                  opacity: (isFirst || (!userIsAdmin && (!user || task.assigned_to !== user.id))) ? 0.5 : 1
                                                }}
                                                title={!userIsAdmin && (!user || task.assigned_to !== user.id) ? 'Read-only: Task not assigned to you' : isFirst ? 'Cannot move up - already first' : 'Move up'}
                                              >
                                                ↑
                                              </button>
                                              <button
                                                onClick={() => (userIsAdmin || (user && task.assigned_to === user.id)) && !isLast ? moveTask(task.id, 'down') : null}
                                                disabled={isLast || (!userIsAdmin && (!user || task.assigned_to !== user.id))}
                                                style={{
                                                  fontSize: '1rem',
                                                  padding: '0.5rem 0.75rem',
                                                  backgroundColor: (isLast || (!userIsAdmin && (!user || task.assigned_to !== user.id))) ? '#6c757d' : '#007bff',
                                                  color: 'white',
                                                  border: 'none',
                                                  borderRadius: '4px',
                                                  cursor: (isLast || (!userIsAdmin && (!user || task.assigned_to !== user.id))) ? 'not-allowed' : 'pointer',
                                                  opacity: (isLast || (!userIsAdmin && (!user || task.assigned_to !== user.id))) ? 0.5 : 1
                                                }}
                                                title={!userIsAdmin && (!user || task.assigned_to !== user.id) ? 'Read-only: Task not assigned to you' : isLast ? 'Cannot move down - already last' : 'Move down'}
                                              >
                                                ↓
                                              </button>
                                            </>
                                          );
                                        })()} 
                                        
                                        <button 
                                          onClick={() => handleViewPhotos(task.id)} 
                                          className="btn btn-success"
                                          style={{ fontSize: '0.875rem', padding: '0.4rem 0.8rem' }}
                                        >
                                          📷 Photos
                                        </button>
                                        
                                        {/* Edit Button - Admins: always enabled, Engineers: only for their tasks */}
                                        <button 
                                          onClick={() => userIsAdmin || (user && task.assigned_to === user.id) ? handleEditTask(task) : null}
                                          className={`btn ${userIsAdmin || (user && task.assigned_to === user.id) ? 'btn-primary' : 'btn-secondary'}`}
                                          disabled={!userIsAdmin && (!user || task.assigned_to !== user.id)}
                                          style={{ 
                                            fontSize: '0.875rem', 
                                            padding: '0.4rem 0.8rem',
                                            opacity: !userIsAdmin && (!user || task.assigned_to !== user.id) ? 0.5 : 1,
                                            cursor: !userIsAdmin && (!user || task.assigned_to !== user.id) ? 'not-allowed' : 'pointer'
                                          }}
                                          title={!userIsAdmin && (!user || task.assigned_to !== user.id) ? 'Read-only: Task not assigned to you' : 'Edit task'}
                                        >
                                          Edit
                                        </button>
                                        
                                        {/* Delete Button - Admins only */}
                                        {userIsAdmin && (
                                          <button 
                                            onClick={() => handleDeleteTask(task.id)} 
                                            className="btn btn-danger"
                                            style={{ fontSize: '0.875rem', padding: '0.4rem 0.8rem' }}
                                          >
                                            Delete
                                          </button>
                                        )}
                                       </div>
                                    </div>
                                  );
                                })}
                              </div>
                              );
                            });
                          })()
                        ) : (
                          // Regular date-grouped view
                          groupedTasks[date].map((task, index) => {
                       const isCompleted = task.status.toLowerCase() === 'completed';
                       
                       return (
                         <div 
                           key={task.id} 
                           className="task-list-item" 
                           style={{ 
                             padding: '0.75rem',
                             borderBottom: index < groupedTasks[date].length - 1 ? '1px solid #e9ecef' : 'none',
                             display: 'flex',
                             flexDirection: 'column',
                             gap: '0.5rem',
                             backgroundColor: 'transparent',
                             opacity: isCompleted ? 0.6 : 1,
                             transition: 'opacity 0.3s ease'
                           }}
                         >
                        {/* Task Header */}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '0.5rem' }}>
                          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
                            {/* Order Position Number */}
                            <div style={{
                              backgroundColor: '#007bff',
                              color: 'white',
                              borderRadius: '50%',
                              width: '28px',
                              height: '28px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '0.875rem',
                              fontWeight: 'bold',
                              flexShrink: 0
                            }}>
                              {(() => {
                                // Calculate position within engineer's tasks for this date
                                const tasksByEngineer = groupedTasks[date].reduce((groups: { [key: string]: Task[] }, t) => {
                                  const engineerKey = t.assigned_user 
                                    ? `${t.assigned_user.first_name} ${t.assigned_user.last_name}`
                                    : 'Unassigned';
                                  if (!groups[engineerKey]) groups[engineerKey] = [];
                                  groups[engineerKey].push(t);
                                  return groups;
                                }, {});
                                
                                const currentEngineerKey = task.assigned_user 
                                  ? `${task.assigned_user.first_name} ${task.assigned_user.last_name}`
                                  : 'Unassigned';
                                
                                const engineerTasks = tasksByEngineer[currentEngineerKey]?.sort((a, b) => a.order_position - b.order_position) || [];
                                const engineerIndex = engineerTasks.findIndex(t => t.id === task.id);
                                return engineerIndex + 1;
                              })()
                            }
                            </div>
                            {/* Task Info */}
                            <div>
                              <h4 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 'bold', color: '#2c3e50' }}>
                                {task.title}
                              </h4>
                              <p style={{ margin: '0.25rem 0 0 0', color: '#6c757d', fontSize: '0.9rem' }}>
                                {task.client}
                              </p>
                            </div>
                          </div>
                          
                          {/* Status Selector */}
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                              <span style={{ fontSize: '0.875rem', color: '#666' }}>Status:</span>
                              <select
                                value={task.status}
                                onChange={(e) => userIsAdmin || (user && task.assigned_to === user.id) ? handleStatusChange(task.id, e.target.value) : null}
                                className="status-select"
                                disabled={!userIsAdmin && (!user || task.assigned_to !== user.id)}
                                style={{
                                  padding: '0.25rem 0.5rem',
                                  borderRadius: '4px',
                                  border: '1px solid #ddd',
                                  fontSize: '0.875rem',
                                  backgroundColor: !userIsAdmin && (!user || task.assigned_to !== user.id) ? '#f8f9fa' : 'white',
                                  opacity: !userIsAdmin && (!user || task.assigned_to !== user.id) ? 0.6 : 1,
                                  cursor: !userIsAdmin && (!user || task.assigned_to !== user.id) ? 'not-allowed' : 'pointer'
                                }}
                                title={!userIsAdmin && (!user || task.assigned_to !== user.id) ? 'Read-only: Task not assigned to you' : 'Change task status'}
                              >
                                {userIsAdmin ? (
                                  <>
                                    <option value="TBC">TBC</option>
                                    <option value="Booked">Booked</option>
                                    <option value="Completed">Completed</option>
                                    <option value="Invoiced">Invoiced</option>
                                  </>
                                ) : (
                                  <>
                                    <option value="Booked">Booked</option>
                                    <option value="Completed">Completed</option>
                                    {/* Show current status even if not in allowed list */}
                                    {task.status !== 'Booked' && task.status !== 'Completed' && (
                                      <option value={task.status} disabled>{task.status} (Read-only)</option>
                                    )}
                                  </>
                                )}
                              </select>
                            </div>
                            
                            {/* Invoice Number Field - Only show when status is Invoiced */}
                            {task.status.toLowerCase() === 'invoiced' && (
                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                <span style={{ fontSize: '0.875rem', color: '#666' }}>Invoice Number:</span>
                                <input
                                  type="text"
                                  value={task.invoice_number || ''}
                                  onChange={async (e) => {
                                    if (userIsAdmin) {
                                      try {
                                        await axios.patch(`${API_BASE_URL}/api/tasks/${task.id}`, {
                                          invoice_number: e.target.value
                                        });
                                        fetchTasks();
                                      } catch (error: any) {
                                        console.error('Failed to update invoice number:', error);
                                        alert('Failed to update invoice number');
                                      }
                                    }
                                  }}
                                  disabled={!userIsAdmin}
                                  style={{
                                    padding: '0.25rem 0.5rem',
                                    borderRadius: '4px',
                                    border: '1px solid #ddd',
                                    fontSize: '0.875rem',
                                    backgroundColor: !userIsAdmin ? '#f8f9fa' : 'white',
                                    opacity: !userIsAdmin ? 0.6 : 1,
                                    cursor: !userIsAdmin ? 'not-allowed' : 'text',
                                    minWidth: '120px'
                                  }}
                                  placeholder="Enter invoice number"
                                  title={!userIsAdmin ? 'Read-only: Admin access required' : 'Enter invoice number'}
                                />
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Address */}
                        <div style={{ fontSize: '0.9rem', color: '#495057' }}>
                          <div>{task.address_line1}</div>
                          {task.address_line2 && <div>{task.address_line2}</div>}
                          <div>{task.postcode}</div>
                        </div>

                        {/* Time */}
                        {(task.start_time || task.end_time) && (
                          <div style={{ 
                            fontSize: '0.9rem', 
                            color: '#3498db', 
                            fontWeight: '500' 
                          }}>
                            🕒 {task.start_time ? formatTime(task.start_time) : ''} 
                            {task.start_time && task.end_time && ' - '}
                            {task.end_time ? formatTime(task.end_time) : ''}
                          </div>
                        )}

                        {/* Description */}
                        {task.description && (
                          <div style={{ 
                            fontSize: '0.9rem', 
                            color: '#495057',
                            backgroundColor: '#f8f9fa',
                            padding: '0.5rem',
                            borderRadius: '4px',
                            borderLeft: '3px solid #6c757d'
                          }}>
                            {task.description}
                          </div>
                        )}

                        {/* Engineer Notes */}
                        {task.engineer_notes && (
                          <div style={{ 
                            fontSize: '0.9rem', 
                            color: '#495057',
                            backgroundColor: '#e8f4f8',
                            padding: '0.5rem',
                            borderRadius: '4px',
                            borderLeft: '3px solid #17a2b8'
                          }}>
                            <div style={{ marginBottom: shouldTruncateNotes(task.engineer_notes) && !expandedNotes.has(task.id) ? '0.5rem' : '0' }}>
                              <strong style={{ color: '#17a2b8' }}>Engineer Notes:</strong>{' '}
                              {expandedNotes.has(task.id) || !shouldTruncateNotes(task.engineer_notes) 
                                ? task.engineer_notes 
                                : getTruncatedNotes(task.engineer_notes)
                              }
                            </div>
                            {shouldTruncateNotes(task.engineer_notes) && (
                              <button
                                onClick={() => toggleNotesExpansion(task.id)}
                                style={{
                                  background: 'none',
                                  border: 'none',
                                  color: '#17a2b8',
                                  fontSize: '0.875rem',
                                  cursor: 'pointer',
                                  textDecoration: 'underline',
                                  padding: '0',
                                  fontWeight: '500'
                                }}
                              >
                                {expandedNotes.has(task.id) ? 'Read less' : 'Read more'}
                              </button>
                            )}
                          </div>
                        )}

                        {/* Assignment */}
                        <div style={{ fontSize: '0.875rem', color: '#6c757d' }}>
                          {task.assigned_user ? 
                            `👤 Assigned to: ${task.assigned_user.first_name} ${task.assigned_user.last_name}` : 
                            '👤 Unassigned'
                          }
                        </div>

                        {/* Action Buttons */}
                        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginTop: '0.5rem', justifyContent: 'flex-end' }}>
                          {/* Task Ordering Buttons */}
                          {(() => {
                            // Group tasks by engineer within this date, then find current task's position within their group
                            const tasksByEngineer = groupedTasks[date].reduce((groups: { [key: string]: Task[] }, t) => {
                              const engineerKey = t.assigned_user 
                                ? `${t.assigned_user.first_name} ${t.assigned_user.last_name}`
                                : 'Unassigned';
                              if (!groups[engineerKey]) groups[engineerKey] = [];
                              groups[engineerKey].push(t);
                              return groups;
                            }, {});
                            
                            const currentEngineerKey = task.assigned_user 
                              ? `${task.assigned_user.first_name} ${task.assigned_user.last_name}`
                              : 'Unassigned';
                            
                            const engineerTasks = tasksByEngineer[currentEngineerKey]?.sort((a, b) => a.order_position - b.order_position) || [];
                            const currentIndex = engineerTasks.findIndex(t => t.id === task.id);
                            const isFirst = currentIndex === 0;
                            const isLast = currentIndex === engineerTasks.length - 1;
                            
                            return (
                              <>
                                <button
                                  onClick={() => (userIsAdmin || (user && task.assigned_to === user.id)) && !isFirst ? moveTask(task.id, 'up') : null}
                                  disabled={isFirst || (!userIsAdmin && (!user || task.assigned_to !== user.id))}
                                  style={{
                                    fontSize: '1rem',
                                    padding: '0.5rem 0.75rem',
                                    backgroundColor: (isFirst || (!userIsAdmin && (!user || task.assigned_to !== user.id))) ? '#6c757d' : '#007bff',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: (isFirst || (!userIsAdmin && (!user || task.assigned_to !== user.id))) ? 'not-allowed' : 'pointer',
                                    opacity: (isFirst || (!userIsAdmin && (!user || task.assigned_to !== user.id))) ? 0.5 : 1
                                  }}
                                  title={!userIsAdmin && (!user || task.assigned_to !== user.id) ? 'Read-only: Task not assigned to you' : isFirst ? 'Cannot move up - already first' : 'Move up'}
                                >
                                  ↑
                                </button>
                                <button
                                  onClick={() => (userIsAdmin || (user && task.assigned_to === user.id)) && !isLast ? moveTask(task.id, 'down') : null}
                                  disabled={isLast || (!userIsAdmin && (!user || task.assigned_to !== user.id))}
                                  style={{
                                    fontSize: '1rem',
                                    padding: '0.5rem 0.75rem',
                                    backgroundColor: (isLast || (!userIsAdmin && (!user || task.assigned_to !== user.id))) ? '#6c757d' : '#007bff',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: (isLast || (!userIsAdmin && (!user || task.assigned_to !== user.id))) ? 'not-allowed' : 'pointer',
                                    opacity: (isLast || (!userIsAdmin && (!user || task.assigned_to !== user.id))) ? 0.5 : 1
                                  }}
                                  title={!userIsAdmin && (!user || task.assigned_to !== user.id) ? 'Read-only: Task not assigned to you' : isLast ? 'Cannot move down - already last' : 'Move down'}
                                >
                                  ↓
                                </button>
                              </>
                            );
                          })()} 
                          
                          <button 
                            onClick={() => handleViewPhotos(task.id)} 
                            className="btn btn-success"
                            style={{ fontSize: '0.875rem', padding: '0.4rem 0.8rem' }}
                          >
                            📷 Photos
                          </button>
                          
                          {/* Edit Button - Admins: always enabled, Engineers: only for their tasks */}
                          <button 
                            onClick={() => userIsAdmin || (user && task.assigned_to === user.id) ? handleEditTask(task) : null}
                            className={`btn ${userIsAdmin || (user && task.assigned_to === user.id) ? 'btn-primary' : 'btn-secondary'}`}
                            disabled={!userIsAdmin && (!user || task.assigned_to !== user.id)}
                            style={{ 
                              fontSize: '0.875rem', 
                              padding: '0.4rem 0.8rem',
                              opacity: !userIsAdmin && (!user || task.assigned_to !== user.id) ? 0.5 : 1,
                              cursor: !userIsAdmin && (!user || task.assigned_to !== user.id) ? 'not-allowed' : 'pointer'
                            }}
                            title={!userIsAdmin && (!user || task.assigned_to !== user.id) ? 'Read-only: Task not assigned to you' : 'Edit task'}
                          >
                            Edit
                          </button>
                          
                          {/* Delete Button - Admins only */}
                          {userIsAdmin && (
                            <button 
                              onClick={() => handleDeleteTask(task.id)} 
                              className="btn btn-danger"
                              style={{ fontSize: '0.875rem', padding: '0.4rem 0.8rem' }}
                            >
                              Delete
            </button>
                          )}
                         </div>
                       </div>
                         );
                      })
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          );
        })()}

        {userIsAdmin && (
          <button
            onClick={() => setIsModalOpen(true)}
            className="add-task-btn"
            title="Add New Task"
          >
            +
          </button>
        )}

        <TaskModal
          isOpen={isModalOpen}
          onClose={() => {
            setIsModalOpen(false);
            setEditingTask(undefined);
          }}
          onSubmit={handleTaskSubmit}
          task={editingTask}
          users={users}
        />

        <PhotoUpload
          taskId={selectedTaskId || 0}
          isOpen={isPhotoModalOpen}
          onClose={() => {
            setIsPhotoModalOpen(false);
            setSelectedTaskId(null);
          }}
        />
      </div>
    </div>
  );
};

export default Dashboard;